import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { PermissionService } from '@app/core/services/permission.service';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ApprovalDetailsDialogComponent } from './components/shared/approval-details-dialog/approval-details-dialog.component';
import { APPROVAL_DETAILS_CONFIG } from './components/shared/approval-details-dialog/approval-details-dialog.config';
import {
  SYNCHRONIZATION_SEARCH_CONFIG,
  SYNCHRONIZATION_COLUMNS,
  SYNCHRONIZATION_DEFAULT_COLUMNS_ORDER,
  SYNCHRONIZATION_FLEXIBLE_COLUMNS_ORDER,
  SYNCHRONIZATION_ACTION_COLUMN,
  getStatusSeverity,
  getStatusDisplayText,
  getStatusStyle,
  createRejectDialogConfig,
  createDeleteDialogConfig,
  createBatchDeleteDialogConfig,
  DialogConfig,
} from './synchronization.config';
import { SynchronizationService } from './services/synchronization.service';
import { AdjustDetailsReqDTO } from '@app/api/stock-service/model/adjustDetailsReqDTO';
import {
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { SynchronizationItem } from './models/synchronization.model';

@Component({
  selector: 'app-synchronization',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    ApprovalDetailsDialogComponent,
    AppButtonComponent,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './synchronization.component.html',
  styleUrls: ['./synchronization.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SynchronizationComponent implements OnInit, OnDestroy {
  // Search configuration
  searchConfig = SYNCHRONIZATION_SEARCH_CONFIG;

  // Check if user has any action permissions (UPDATE, DELETE, or READ for view)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canDelete() || this.canRead(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      SYNCHRONIZATION_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of SYNCHRONIZATION_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(SYNCHRONIZATION_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      SYNCHRONIZATION_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of SYNCHRONIZATION_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Reference to approval details dialog, needed to reset its internal loading
  // state on approve/reject failure (the dialog manages that state itself).
  @ViewChild(ApprovalDetailsDialogComponent)
  approvalDetailsDialog?: ApprovalDetailsDialogComponent;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'inventory_adjustment' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_adjustment.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_adjustment.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_adjustment.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_adjustment.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_adjustment.approve');
  });

  primaryButton = computed(() => ({
    label: 'common.actions.new',
    severity: 'info' as const,
    show: this.canCreate(),
    icon: 'add',
  }));

  secondaryButton = computed(() => ({
    label: 'common.actions.delete',
    severity: 'danger' as const,
    show: this.canDelete(),
    icon: 'delete',
    disabled: !this.hasDeletableSynchronizations(),
  }));

  // Data signals
  synchronizations = signal<SynchronizationItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    warehouseCode: '',
    warehouseName: '',
    status: undefined,
    syncTime: null,
    page: 1,
    size: 20,
  });

  // Selection
  selectedSynchronizations = signal<SynchronizationItem[]>([]);

  // Computed properties
  hasSelectedSynchronizations = computed(
    () => this.selectedSynchronizations().length > 0,
  );
  hasDeletableSynchronizations = computed(() => {
    const selected = this.selectedSynchronizations();
    return (
      selected.length > 0 &&
      selected.some(
        (sync) => sync.status === 'success' || sync.status === 'fail',
      )
    );
  });

  // Computed dialog configuration
  dialogConfig = computed((): DialogConfig | null => {
    const selectedSync = this.selectedSynchronization();
    const action = this.currentAction();

    if (!selectedSync || !action) return null;

    switch (action) {
      case 'reject':
        return createRejectDialogConfig(
          selectedSync.adjustCode,
          this.translateService,
        );
      case 'delete':
        return createDeleteDialogConfig(
          selectedSync.adjustCode,
          this.translateService,
        );
      case 'batchDelete':
        const deletableCount = this.selectedSynchronizations().filter(
          (sync) => sync.status === 'success' || sync.status === 'fail',
        ).length;
        return createBatchDeleteDialogConfig(
          deletableCount,
          this.translateService,
        );
      default:
        return null;
    }
  });

  // Confirmation dialog properties
  showConfirmationDialog = signal(false);
  selectedSynchronization = signal<SynchronizationItem | null>(null);
  currentAction = signal<
    'approve' | 'reject' | 'delete' | 'batchDelete' | null
  >(null);

  // Approval details dialog properties
  showApprovalDetailsDialog = false;
  approvalDetailsConfig = APPROVAL_DETAILS_CONFIG;
  approvalDetailsData: any = null;
  approvalDetailsLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly synchronizationService: SynchronizationService,
    private readonly router: Router,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // Helper methods for message display
  private showSuccessMessage(key: string, params?: any): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant(
        'synchronization.messages.success.title',
      ),
      detail: this.translateService.instant(key, params),
      life: 3000,
    });
  }

  private showErrorMessage(key: string, params?: any): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        'synchronization.messages.error.title',
      ),
      detail: this.translateService.instant(key, params),
      life: 3000,
    });
  }

  private showWarningMessage(key: string, params?: any): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant(
        'synchronization.messages.warning.title',
      ),
      detail: this.translateService.instant(key, params),
      life: 3000,
    });
  }

  // Maps a backend business errorCode to a translated (i18n) message key, so
  // the toast always shows in the user's active language instead of the raw
  // English message the backend sends - only the errorCode is stable/parseable,
  // the free-text message is not localized and must not be shown as-is. The
  // "_for_sku" variant is used when a SKU code could be extracted from the
  // backend message (see extractSkuCode), so a multi-SKU adjustment record
  // tells the user which specific SKU failed instead of just "insufficient
  // stock" with no way to tell which line item is the problem.
  private static readonly API_ERROR_CODE_KEYS: Record<
    string,
    { plain: string; forSku: string }
  > = {
    INSUFFICIENT_STOCK: {
      plain: 'synchronization.messages.error.insufficient_stock',
      forSku: 'synchronization.messages.error.insufficient_stock_for_sku',
    },
    NEGATIVE_STOCK_NOT_ALLOWED: {
      plain: 'synchronization.messages.error.negative_stock_not_allowed',
      forSku:
        'synchronization.messages.error.negative_stock_not_allowed_for_sku',
    },
    // Thrown by GoodsStockDetailRetryHelper when the DB constraint
    // (chk_goods_stock_availability) rejects a write that NegativeStockGuard's
    // app-level pre-check didn't catch (e.g. reservedNum/lockNum insufficient
    // to fully offset the deduction) - still a "would go negative" case from
    // the user's perspective, so reuse the same wording as NEGATIVE_STOCK_NOT_ALLOWED.
    STOCK_CONSTRAINT_VIOLATION: {
      plain: 'synchronization.messages.error.negative_stock_not_allowed',
      forSku:
        'synchronization.messages.error.negative_stock_not_allowed_for_sku',
    },
  };

  // Backend messages for all of the errorCodes above are built server-side
  // as "... for SKU <code> in warehouse <code>. ..." (GoodsStockServiceImpl
  // .validateStockAvailability / NegativeStockGuard.assertNonNegative /
  // GoodsStockDetailRetryHelper) - not structured data, but a stable enough
  // pattern (backend-controlled, not user input) to pull the SKU code out of
  // for display purposes only.
  private static extractSkuCode(message: unknown): string | undefined {
    if (typeof message !== 'string') {
      return undefined;
    }
    return /for SKU (\S+) in warehouse/.exec(message)?.[1];
  }

  // Shows a localized error message for the failed approve/reject call. Reads
  // errorCode (and, best-effort, the SKU code) from the response body to pick
  // the right translation, falling back to fallbackKey for any errorCode this
  // component doesn't know about (e.g. network error, or a business error not
  // specific to this screen).
  // The generated API client defaults to Accept: */*, which makes Angular
  // deliver the error body as a Blob instead of parsed JSON - must go through
  // BlobConverterUtil like every other call site in this codebase that reads
  // a response body (see blob-converter.util.ts).
  private async showApiErrorMessage(
    error: unknown,
    fallbackKey: string,
  ): Promise<void> {
    const rawBody = (error as { error?: unknown })?.error;
    let errorCode: string | undefined;
    let skuCode: string | undefined;
    try {
      const parsed = await BlobConverterUtil.convertToJson(rawBody);
      errorCode = parsed?.errorCode;
      skuCode = SynchronizationComponent.extractSkuCode(parsed?.message);
    } catch (parseError) {
      console.error('Error parsing API error body:', parseError);
    }
    const keys =
      errorCode && SynchronizationComponent.API_ERROR_CODE_KEYS[errorCode];
    const detail = keys
      ? this.translateService.instant(
          skuCode ? keys.forSku : keys.plain,
          skuCode ? { skuCode } : undefined,
        )
      : this.translateService.instant(fallbackKey);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        'synchronization.messages.error.title',
      ),
      detail,
      life: 5000,
    });
  }

  ngOnInit(): void {
    this.loadSynchronizations();

    // Subscribe to synchronization operation results from add/edit components
    this.synchronizationService.synchronizationOperationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.onSynchronizationOperationResult(result);
          this.synchronizationService.clearSynchronizationOperationResult();
        }
      });
  }

  onSearch(params: any): void {
    this.searchParams.set({ ...this.searchParams(), ...params, page: 1 });
    this.loadSynchronizations();
  }

  onReset(): void {
    // Check if there's any data to reset
    const currentParams = this.searchParams();
    const hasDataToReset =
      currentParams.warehouseCode ||
      currentParams.warehouseName ||
      currentParams.status ||
      currentParams.syncTime;

    if (!hasDataToReset) {
      return;
    }

    // Reset search parameters to default values
    this.searchParams.set({
      warehouseCode: '',
      warehouseName: '',
      status: undefined,
      syncTime: null,
      page: 1,
      size: 20,
    });

    // Clear any selections
    this.selectedSynchronizations.set([]);

    // Reload synchronizations with default parameters
    this.loadSynchronizations();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadSynchronizations();
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  onSelectionChange(selection: SynchronizationItem[]): void {
    this.selectedSynchronizations.set(selection);
  }

  onAdd(): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }

    this.router.navigate(['/stock/synchronization/add']);
  }

  onEdit(synchronization: SynchronizationItem): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }

    this.router.navigate(['/stock/synchronization/edit'], {
      queryParams: { id: synchronization.id },
    });
  }

  onView(synchronization: SynchronizationItem): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }

    this.router.navigate(['/stock/synchronization/view'], {
      queryParams: { id: synchronization.id },
    });
  }

  // Handle synchronization operation results from add/edit components
  onSynchronizationOperationResult(event: {
    success: boolean;
    message: string;
    type: 'create' | 'update' | 'approve';
  }): void {
    if (event.success) {
      this.showSuccessMessage(event.message);
      this.loadSynchronizations();
    } else {
      this.showErrorMessage(event.message);
    }
  }

  onReview(synchronization: SynchronizationItem): void {
    // Check permission before reviewing
    if (!this.canApprove()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }

    this.selectedSynchronization.set(synchronization);
    this.approvalDetailsData = null;
    this.approvalDetailsLoading = true;
    this.showApprovalDetailsDialog = true;
    this.cdr.detectChanges();
    this.loadApprovalDetails(String(synchronization.id));
  }

  onDelete(synchronization: SynchronizationItem): void {
    // Check permission before deleting
    if (!this.canDelete()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }

    this.selectedSynchronization.set(synchronization);
    this.currentAction.set('delete');
    this.showConfirmationDialog.set(true);
  }

  onBatchDelete(): void {
    // Check permission before batch deleting
    if (!this.canDelete()) {
      this.showWarningMessage('common.permissions.no_permission');
      return;
    }
    const selectedSynchronizations = this.selectedSynchronizations();
    const deletableSynchronizations = selectedSynchronizations.filter(
      (sync) => sync.status === 'success' || sync.status === 'fail',
    );

    if (deletableSynchronizations.length === 0) {
      this.showWarningMessage(
        'synchronization.messages.warning.no_deletable_synchronizations',
      );
      return;
    }

    // Set the first deletable item for dialog config (needs adjustCode for message)
    this.selectedSynchronization.set(deletableSynchronizations[0]);
    this.currentAction.set('batchDelete');
    this.showConfirmationDialog.set(true);
  }

  private loadSynchronizations(): void {
    this.loading.set(true);
    this.synchronizationService
      .getSynchronizations(this.searchParams())
      .subscribe({
        next: (response) => {
          // Map createDate from API response to creationDate for table display
          const mappedData = response.data.map((item: any) => ({
            ...item,
            creationDate: item.createDate || item.creationDate,
          }));
          this.synchronizations.set(mappedData);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading synchronizations:', error);
          this.loading.set(false);
          this.showErrorMessage(
            'synchronization.messages.error.failed_to_load_synchronizations',
          );
        },
      });
  }

  private loadApprovalDetails(id: string): void {
    this.approvalDetailsLoading = true;
    this.cdr.detectChanges();
    this.synchronizationService
      .getSynchronizationDetailsForApproval(id)
      .subscribe({
        next: (response) => {
          this.approvalDetailsData = response;
          this.approvalDetailsLoading = false;
          this.showApprovalDetailsDialog = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading approval details:', error);
          this.approvalDetailsLoading = false;
          this.cdr.detectChanges();
          this.showErrorMessage(
            'synchronization.messages.error.failed_to_load_approval_details',
          );
        },
      });
  }

  private approveSynchronization(
    id: number,
    adjustDetailsReqDTOList?: AdjustDetailsReqDTO[],
    typeCode?: string,
  ): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.synchronizationService
      .synchronizationStatus(id, 'success', typeCode, adjustDetailsReqDTOList)
      .subscribe({
        next: (success) => {
          this.loading.set(false);
          if (success) {
            this.showSuccessMessage(
              'synchronization.messages.success.synchronization_approved',
            );
            // Add delay to allow backend to process the status change, then close dialog and refresh table
            setTimeout(() => {
              this.showApprovalDetailsDialog = false;
              this.approvalDetailsData = null;
              this.loadSynchronizations();
            }, 1000);
          } else {
            this.showErrorMessage(
              'synchronization.messages.error.failed_to_approve_synchronization',
            );
            // Reset dialog loading state on error
            this.approvalDetailsLoading = false;
            this.approvalDetailsDialog?.resetLoadingState();
          }
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error approving synchronization:', error);
          void this.showApiErrorMessage(
            error,
            'synchronization.messages.error.failed_to_approve_synchronization',
          );
          // Reset dialog loading state on error
          this.approvalDetailsLoading = false;
          this.approvalDetailsDialog?.resetLoadingState();
        },
      });
  }

  private rejectSynchronization(id: number): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.synchronizationService.synchronizationStatus(id, 'fail').subscribe({
      next: (success) => {
        this.loading.set(false);
        if (success) {
          this.showSuccessMessage(
            'synchronization.messages.success.synchronization_rejected',
          );
          // Add delay to allow backend to process the status change, then close dialog and refresh table
          setTimeout(() => {
            this.showApprovalDetailsDialog = false;
            this.approvalDetailsData = null;
            this.loadSynchronizations();
          }, 1000);
        } else {
          this.showErrorMessage(
            'synchronization.messages.error.failed_to_reject_synchronization',
          );
          // Reset dialog loading state on error
          this.approvalDetailsLoading = false;
          this.approvalDetailsDialog?.resetLoadingState();
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error rejecting synchronization:', error);
        void this.showApiErrorMessage(
          error,
          'synchronization.messages.error.failed_to_reject_synchronization',
        );
        // Reset dialog loading state on error
        this.approvalDetailsLoading = false;
        this.approvalDetailsDialog?.resetLoadingState();
      },
    });
  }

  private deleteSynchronization(id: number): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.synchronizationService.batchDeleteSynchronizations([id]).subscribe({
      next: (success) => {
        this.loading.set(false);
        if (success) {
          this.showSuccessMessage(
            'synchronization.messages.success.synchronization_deleted',
          );
          // Add delay to allow backend to process the deletion
          setTimeout(() => {
            this.loadSynchronizations();
          }, 1000);
        } else {
          this.showErrorMessage(
            'synchronization.messages.error.failed_to_delete_synchronization',
          );
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error deleting synchronization:', error);
        this.showErrorMessage(
          'synchronization.messages.error.failed_to_delete_synchronization',
        );
      },
    });
  }

  private batchDeleteSynchronizations(ids: number[]): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.synchronizationService.batchDeleteSynchronizations(ids).subscribe({
      next: (success) => {
        this.loading.set(false);
        if (success) {
          this.showSuccessMessage(
            'synchronization.messages.success.synchronizations_deleted',
            { count: ids.length },
          );
          this.selectedSynchronizations.set([]);
          // Button state is now computed reactively, no need to manually update
          // Add delay to allow backend to process the batch deletion
          setTimeout(() => {
            this.loadSynchronizations();
          }, 1000);
        } else {
          this.showErrorMessage(
            'synchronization.messages.error.failed_to_delete_synchronizations',
          );
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error batch deleting synchronizations:', error);
        this.showErrorMessage(
          'synchronization.messages.error.failed_to_delete_synchronizations',
        );
      },
    });
  }

  // Status methods
  getStatusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return this.translateService.instant(getStatusDisplayText(status));
  }

  getStatusStyle(status: string): any {
    return getStatusStyle(status);
  }

  formatDateTime = formatDateTime;

  // Function to check if a row should be disabled for selection
  isRowDisabledFn = (rowData: SynchronizationItem): boolean => {
    // Disable selection for items that cannot be deleted (status is 'wait' or 'pending')
    return rowData.status === 'wait' || rowData.status === 'pending';
  };

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction() === 'reject') {
        this.confirmReject();
      } else if (this.currentAction() === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction() === 'batchDelete') {
        this.confirmBatchDelete();
      }
    }
  }

  confirmReject(): void {
    const selectedSync = this.selectedSynchronization();
    if (selectedSync) {
      this.rejectSynchronization(selectedSync.id as number);
    }
    this.showApprovalDetailsDialog = false;
    this.closeConfirmationDialog();
  }

  confirmDelete(): void {
    const selectedSync = this.selectedSynchronization();
    if (selectedSync) {
      this.deleteSynchronization(selectedSync.id as number);
    }
    this.closeConfirmationDialog();
  }

  confirmBatchDelete(): void {
    const selectedItems = this.selectedSynchronizations();
    const deletableItems = selectedItems.filter(
      (sync) => sync.status === 'success' || sync.status === 'fail',
    );
    const nonDeletableItems = selectedItems.filter(
      (sync) => sync.status !== 'success' && sync.status !== 'fail',
    );

    // Show warning if there are non-deletable items
    if (nonDeletableItems.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'synchronization.messages.warning.title',
        ),
        detail: this.translateService.instant(
          'synchronization.messages.warning.cannot_delete_non_success_fail',
          { count: nonDeletableItems.length },
        ),
        life: 5000,
      });
    }

    // Delete only success and fail items
    if (deletableItems.length > 0) {
      const ids = deletableItems.map((sync) => sync.id as number);
      this.batchDeleteSynchronizations(ids);
    }

    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedSynchronization.set(null);
    this.currentAction.set(null);
  }

  // Approval details dialog event handlers
  onApprovalDetailsDialogVisibleChange(visible: boolean): void {
    this.showApprovalDetailsDialog = visible;
    if (!visible) {
      this.approvalDetailsData = null;
      this.approvalDetailsLoading = false;
      // Reset loading state when dialog is closed
      this.loading.set(false);
    }
    this.cdr.detectChanges();
  }

  onApprovalDetailsApprove(data: any): void {
    if (data && data.synchronizationData && data.synchronizationData.id) {
      // Convert adjustment details to API format
      const adjustDetailsReqDTOList =
        data.adjustmentDetails?.map((detail: any) => ({
          skuCode: detail.skuCode,
          type: detail.adjustmentType === 'INCREASE' ? 1 : 0,
          num: detail.realityNum,
          batchId: detail.batchId || undefined,
        })) || [];

      this.approveSynchronization(
        data.synchronizationData.id,
        adjustDetailsReqDTOList,
        data.synchronizationData.typeCode,
      );
    }
  }

  onApprovalDetailsReject(data: any): void {
    if (data && data.id) {
      this.selectedSynchronization.set(data);
      this.currentAction.set('reject');
      this.showConfirmationDialog.set(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
